import "./WindowForm.css";
import { useState } from "react";
import { api } from "../../api/client";
import TextField from "../TextField/TextField";
import ConfirmAction from "../ConfirmAction/ConfirmAction";
import PreviewFrame from "../PreviewFrame/PreviewFrame";
import SubmissionWindowPreview from "../../preview/participant/components/SubmissionWindow/SubmissionWindow";
import { SUBMISSION_KINDS } from "../../preview/participant/data/submissions";

function toLocalInput(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromLocalInput(value) {
  if (!value) return null;
  return new Date(value).toISOString();
}

function blankInput() {
  return {
    key: "",
    type: "select",
    label: "",
    help: "",
    required: true,
    placeholder: "Select one",
    options: [{ value: "", label: "" }],
  };
}

export default function WindowForm(props) {
  const existing = props.window;
  const [slot, setSlot] = useState(existing ? existing.slot : "");
  const [kind, setKind] = useState(existing ? existing.kind : "github");
  const [title, setTitle] = useState(existing ? existing.title : "");
  const [cta, setCta] = useState(existing ? existing.cta : "");
  const [brief, setBrief] = useState(existing ? existing.brief || "" : "");
  const [note, setNote] = useState(existing ? existing.note || "" : "");
  const [templateUrl, setTemplateUrl] = useState(existing ? existing.templateUrl || "" : "");
  const [templateLabel, setTemplateLabel] = useState(existing ? existing.templateLabel || "" : "");
  const [opensAt, setOpensAt] = useState(toLocalInput(existing && existing.opensAt));
  const [closesAt, setClosesAt] = useState(toLocalInput(existing && existing.closesAt));
  const [isEnabled, setIsEnabled] = useState(existing ? existing.isEnabled : true);
  const [requiresFinalRound, setRequiresFinalRound] = useState(existing ? existing.requiresFinalRound : false);
  const [position, setPosition] = useState(existing ? String(existing.position) : "1");
  const [inputs, setInputs] = useState(existing && existing.inputs.length ? existing.inputs : []);
  const [error, setError] = useState("");

  function updateInput(index, patch) {
    setInputs((prev) => prev.map((inp, i) => (i === index ? { ...inp, ...patch } : inp)));
  }

  function updateOption(inputIndex, optionIndex, patch) {
    setInputs((prev) =>
      prev.map((inp, i) => {
        if (i !== inputIndex) return inp;
        return {
          ...inp,
          options: inp.options.map((o, oi) => (oi === optionIndex ? { ...o, ...patch } : o)),
        };
      })
    );
  }

  const previewWindow = {
    slot: slot || "slug",
    kind,
    title: title || "Untitled window",
    cta: cta || "Submit",
    brief: brief || null,
    note: note || null,
    templateUrl: templateUrl || null,
    templateLabel: templateUrl ? templateLabel || "View template" : null,
    closesAt: closesAt ? Date.parse(fromLocalInput(closesAt)) : null,
    inputs,
    ...SUBMISSION_KINDS[kind],
  };

  async function save() {
    setError("");
    if (!opensAt || !closesAt) {
      setError("Both an open and a close time are required.");
      throw new Error("Both an open and a close time are required.");
    }
    const payload = {
      slot: slot.trim(),
      kind,
      title: title.trim(),
      cta: cta.trim(),
      brief: brief.trim() || null,
      note: note.trim() || null,
      templateUrl: templateUrl.trim() || null,
      templateLabel: templateLabel.trim() || null,
      opensAt: fromLocalInput(opensAt),
      closesAt: fromLocalInput(closesAt),
      isEnabled,
      requiresFinalRound,
      position: Number(position) || 1,
      inputs: inputs.map((inp) => ({
        ...inp,
        key: inp.key.trim(),
        label: inp.label.trim(),
        help: inp.help.trim() || null,
        placeholder: inp.placeholder.trim() || null,
        options: inp.options.filter((o) => o.value.trim() && o.label.trim()),
      })),
    };
    await api.post("/admin/window", payload);
    if (props.onSaved) props.onSaved();
  }

  return (
    <div className="window-form-layout">
      <div className="window-form col">
        <div className="window-form-row">
          <TextField label="Window slug" value={slot} onChange={setSlot} disabled={Boolean(existing)} mono placeholder="r1_deck" />
          <div className="window-kind-toggle row">
            {["github", "pptx"].map((k) => (
              <button
                key={k}
                type="button"
                className={`team-kind-btn${kind === k ? " team-kind-btn-on" : ""}`}
                onClick={() => setKind(k)}
              >
                {k === "github" ? "Repository" : "Slides"}
              </button>
            ))}
          </div>
        </div>

        <TextField label="Title" value={title} onChange={setTitle} placeholder="Pitch deck" />
        <TextField label="Call to action" value={cta} onChange={setCta} placeholder="Submit deck" />
        <TextField label="Brief" value={brief} onChange={setBrief} placeholder="Problem, approach, demo." />
        <TextField label="Note" value={note} onChange={setNote} placeholder="Optional line under the whole card." />
        <TextField label="Template URL" value={templateUrl} onChange={setTemplateUrl} mono placeholder="https://…" />
        <TextField label="Template label" value={templateLabel} onChange={setTemplateLabel} placeholder="View deck template" />

        <div className="window-form-row">
          <label className="col window-form-datefield">
            <span className="t-label">Opens</span>
            <input type="datetime-local" value={opensAt} onChange={(e) => setOpensAt(e.target.value)} className="window-form-date" />
          </label>
          <label className="col window-form-datefield">
            <span className="t-label">Closes</span>
            <input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} className="window-form-date" />
          </label>
        </div>

        <TextField label="Position" value={position} onChange={setPosition} placeholder="1" />

        <label className="window-form-check row">
          <input type="checkbox" checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
          <span>Enabled</span>
        </label>
        <label className="window-form-check row">
          <input
            type="checkbox"
            checked={requiresFinalRound}
            onChange={(e) => setRequiresFinalRound(e.target.checked)}
          />
          <span>Only shown once the final round is announced</span>
        </label>

        <div className="window-form-inputs col">
          <span className="t-label">Extra inputs</span>
          {inputs.map((inp, index) => (
            <div key={index} className="window-input-editor col">
              <div className="window-form-row">
                <TextField label="Key" value={inp.key} onChange={(v) => updateInput(index, { key: v })} mono placeholder="track" />
                <TextField label="Label" value={inp.label} onChange={(v) => updateInput(index, { label: v })} placeholder="Track" />
              </div>
              <TextField label="Help" value={inp.help} onChange={(v) => updateInput(index, { help: v })} placeholder="Optional" />
              <label className="window-form-check row">
                <input
                  type="checkbox"
                  checked={inp.required}
                  onChange={(e) => updateInput(index, { required: e.target.checked })}
                />
                <span>Required</span>
              </label>

              <div className="window-options col">
                <span className="t-label">Options</span>
                {inp.options.map((opt, oi) => (
                  <div key={oi} className="window-form-row">
                    <TextField label="Value" value={opt.value} onChange={(v) => updateOption(index, oi, { value: v })} mono />
                    <TextField label="Label" value={opt.label} onChange={(v) => updateOption(index, oi, { label: v })} />
                  </div>
                ))}
                <button
                  type="button"
                  className="window-form-linkbtn t-body-sm"
                  onClick={() =>
                    updateInput(index, { options: [...inp.options, { value: "", label: "" }] })
                  }
                >
                  + Add option
                </button>
              </div>

              <button
                type="button"
                className="window-form-linkbtn window-form-linkbtn-danger t-body-sm"
                onClick={() => setInputs((prev) => prev.filter((_, i) => i !== index))}
              >
                Remove this input
              </button>
            </div>
          ))}
          <button
            type="button"
            className="window-form-linkbtn t-body-sm"
            onClick={() => setInputs((prev) => [...prev, blankInput()])}
          >
            + Add input
          </button>
        </div>

        {error ? <p className="t-body-sm team-error-text" role="alert">{error}</p> : null}

        <ConfirmAction
          actionLabel={existing ? "Save changes" : "Create window"}
          confirmLabel={existing ? "Save changes" : "Create window"}
          confirmWord={slot.trim() || "slug"}
          disabled={!slot.trim() || !title.trim() || !cta.trim()}
          summary={`This ${existing ? "updates" : "creates"} the "${slot || "…"}" submission window, visible to every non-eliminated team on a team as soon as it opens.`}
          onConfirm={save}
        />
      </div>

      <PreviewFrame>
        <SubmissionWindowPreview window={previewWindow} value={null} remaining={null} note="Nothing to submit" />
      </PreviewFrame>
    </div>
  );
}
